// src/pages/SuccessPage.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardNavbar from "../components/layout/DashboardNavbar";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoImg from "../assets/logo2.png";

export default function SuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    paymentData,
    customerDetails,
    cart = [],
    chargeFee = 100,
    transactionReference,
    transactionId,
  } = location.state || {};

  const [showReceiptModal, setShowReceiptModal] = useState(false);

  const subtotal = cart.reduce(
    (sum, item) => sum + (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1),
    0
  );
  const total = subtotal + chargeFee;
  const reference = paymentData?.reference || transactionReference || "N/A";
  const rrr = paymentData?.rrr || "—";
  const currentDate = new Date().toLocaleDateString("en-GB");
  const currentTime = new Date().toLocaleTimeString();

  const handleCreateWallet = () => {
    navigate("/register", {
      state: { customerDetails, fromSuccess: true },
    });
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  // ─── Modern Fintech PDF with logo + watermark ───────
  const handleDownloadPDF = async () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const amt = (n) => `NGN ${Number(n).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;

    // ── Watermark ──
    try {
      const img = new Image(); img.src = logoImg;
      await new Promise((res) => { img.onload = res; img.onerror = res; });
      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity: 0.05 }));
      doc.addImage(img, "PNG", W / 2 - 45, H / 2 - 45, 90, 90);
      doc.restoreGraphicsState();
    } catch (_) {}

    // ── Header bar ──
    doc.setFillColor(22, 101, 52);
    doc.rect(0, 0, W, 28, "F");
    try {
      const img = new Image(); img.src = logoImg;
      await new Promise((res) => { img.onload = res; img.onerror = res; });
      doc.addImage(img, "PNG", 8, 4, 20, 20);
    } catch (_) {}
    doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(255, 255, 255);
    doc.text("UCH SELF SERVICE", 32, 13);
    doc.setFontSize(8); doc.setFont("helvetica", "normal");
    doc.text("University College Hospital, Ibadan — QuickPay Receipt", 32, 20);

    // ── Title ──
    doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(22, 101, 52);
    doc.text("OFFICIAL PAYMENT RECEIPT", W / 2, 40, { align: "center" });
    doc.setFillColor(22, 163, 74);
    doc.roundedRect(W / 2 - 20, 44, 40, 8, 2, 2, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(255, 255, 255);
    doc.text("PAYMENT SUCCESSFUL", W / 2, 50, { align: "center" });

    // ── Amount ──
    doc.setFont("helvetica", "bold"); doc.setFontSize(26); doc.setTextColor(22, 101, 52);
    doc.text(amt(total), W / 2, 65, { align: "center" });
    doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(100, 100, 100);
    doc.text("TOTAL PAID (incl. service charge)", W / 2, 72, { align: "center" });

    // ── Details table ──
    autoTable(doc, {
      startY: 80,
      margin: { left: 14, right: 14 },
      head: [["Field", "Details"]],
      body: [
        ["Transaction Reference", reference],
        ["RRR (Remita)", rrr !== "—" ? rrr : "N/A"],
        ["Date & Time", `${currentDate}  ${currentTime}`],
        ["Payment Method", "QuickPay (Remita)"],
        ["Patient Name", `${customerDetails?.patient_first_name || ""} ${customerDetails?.patient_last_name || ""}`.trim() || "N/A"],
        ["Patient ID", customerDetails?.patient_hospital_number || "N/A"],
        ["Patient Email", customerDetails?.patient_email || "N/A"],
        ["Patient Phone", customerDetails?.patient_phone_number || "N/A"],
        ["Payer Name", `${customerDetails?.payer_first_name || ""} ${customerDetails?.payer_last_name || ""}`.trim() || "N/A"],
        ["Payer Email", customerDetails?.payer_email || "N/A"],
        ["Payer Phone", customerDetails?.payer_phone_number || "N/A"],
        ["Description", customerDetails?.transaction_description || "Hospital Service Payment"],
      ],
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [22, 101, 52], textColor: 255, fontStyle: "bold" },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 55, fillColor: [240, 253, 244], textColor: [22, 101, 52] },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });

    let y = (doc.lastAutoTable?.finalY ?? 130) + 8;

    // ── Services ──
    if (cart.length > 0) {
      autoTable(doc, {
        startY: y,
        margin: { left: 14, right: 14 },
        head: [["Service", "Qty", "Amount (NGN)"]],
        body: cart.map((item) => [item.name, item.quantity.toString(), amt(item.price * item.quantity)]),
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [22, 101, 52], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [248, 250, 252] },
      });
      y = (doc.lastAutoTable?.finalY ?? y) + 5;
    }

    // ── Summary ──
    doc.setFillColor(240, 253, 244); doc.setDrawColor(134, 239, 172);
    doc.roundedRect(14, y, W - 28, 28, 3, 3, "FD");
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(40, 40, 40);
    doc.text("Subtotal:", 20, y + 8); doc.text(amt(subtotal), W - 16, y + 8, { align: "right" });
    doc.text("Service Charge:", 20, y + 15); doc.text(amt(chargeFee), W - 16, y + 15, { align: "right" });
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(22, 101, 52);
    doc.text("TOTAL PAID:", 20, y + 24); doc.text(amt(total), W - 16, y + 24, { align: "right" });

    // ── Reference box ──
    const footerY = H - 20;
    doc.setFillColor(22, 101, 52);
    doc.roundedRect(14, footerY - 12, W - 28, 9, 2, 2, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(255, 255, 255);
    doc.text(`TRANSACTION REFERENCE: ${reference}`, W / 2, footerY - 6, { align: "center" });

    // ── Footer ──
    doc.setFont("helvetica", "italic"); doc.setFontSize(7); doc.setTextColor(130, 130, 130);
    doc.text("Computer-generated receipt — no signature required  |  UCH Self Service  |  support@uchselfservice.net", W / 2, footerY + 2, { align: "center" });

    doc.save(`UCH_QuickPay_Receipt_${reference}.pdf`);
  };

  // ─── Print Receipt ────────────────────────────────────────────────────────
  const handlePrintReceipt = () => {
    const printContent = document.getElementById("receipt-content");
    if (!printContent) return;
    const win = window.open("", "_blank", "width=700,height=900");
    win.document.write(`<!DOCTYPE html><html><head><title>UCH Receipt</title>
      <style>body{font-family:sans-serif;padding:24px;color:#111;}
      h3{color:#16a34a;} table{width:100%;border-collapse:collapse;}
      td,th{padding:6px 8px;border-bottom:1px solid #e5e7eb;}
      </style></head><body>${printContent.innerHTML}</body></html>`);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-700 via-green-600 to-green-800 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.1)_25%,rgba(255,255,255,0.1)_50%,transparent_50%,transparent_75%,rgba(255,255,255,0.1)_75%,rgba(255,255,255,0.1))] bg-[length:80px_80px]"></div>
      </div>

      <DashboardNavbar />

      <main className="relative z-10 flex items-center justify-center min-h-screen px-4 py-12">
        <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl p-8 sm:p-12">

          {/* Success Icon */}
          <div className="text-center mb-10">
            <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <svg
                className="w-16 h-16 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={4}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-green-700">
              Payment Successful!
            </h1>
            <p className="text-gray-600 mt-3 text-lg">
              Your payment has been processed successfully
            </p>
          </div>

          {/* Patient Details */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-green-700 mb-4 flex items-center gap-3">
              👤 Patient Details
            </h2>
            <div className="bg-gray-50 rounded-2xl p-5 space-y-3">
              <p className="text-gray-700">
                <span className="font-medium">Name:</span>{" "}
                {customerDetails?.patient_first_name}{" "}
                {customerDetails?.patient_last_name}
              </p>
              <p className="text-gray-700">
                <span className="font-medium">Patient ID:</span>{" "}
                {customerDetails?.patient_hospital_number || "N/A"}
              </p>
              {customerDetails?.patient_email && (
                <p className="text-gray-700">
                  <span className="font-medium">Email:</span>{" "}
                  {customerDetails.patient_email}
                </p>
              )}
              {customerDetails?.patient_phone_number && (
                <p className="text-gray-700">
                  <span className="font-medium">Phone:</span>{" "}
                  {customerDetails.patient_phone_number}
                </p>
              )}
            </div>
          </div>

          {/* Payer Details */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-green-700 mb-4 flex items-center gap-3">
              💳 Payer Details
            </h2>
            <div className="bg-gray-50 rounded-2xl p-5 space-y-3">
              <p className="text-gray-700">
                <span className="font-medium">Name:</span>{" "}
                {customerDetails?.payer_first_name}{" "}
                {customerDetails?.payer_last_name}
              </p>
              <p className="text-gray-700">
                <span className="font-medium">Email:</span>{" "}
                {customerDetails?.payer_email || "N/A"}
              </p>
              <p className="text-gray-700">
                <span className="font-medium">Phone:</span>{" "}
                {customerDetails?.payer_phone_number || "N/A"}
              </p>
            </div>
          </div>

          {/* Transaction Details */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-green-700 mb-4 flex items-center gap-3">
              📄 Transaction Details
            </h2>
            <div className="bg-gray-50 rounded-2xl p-5 space-y-3">
              <div className="flex justify-between items-start">
                <span className="text-gray-500 font-medium text-sm">Transaction Reference</span>
                <span className="font-bold text-[#0d3d2e] font-mono text-sm text-right">{reference}</span>
              </div>
              {rrr !== "—" && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-500 font-medium text-sm">RRR (Remita)</span>
                  <span className="font-bold text-[#0d3d2e] font-mono text-sm text-right">{rrr}</span>
                </div>
              )}
              <div className="flex justify-between items-start">
                <span className="text-gray-500 font-medium text-sm">Description</span>
                <span className="text-gray-700 text-sm text-right max-w-[60%]">
                  {customerDetails?.transaction_description || "Payment for hospital services"}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="mb-12">
            <h2 className="text-xl font-semibold text-green-700 mb-4 flex items-center gap-3">
              💰 Payment Summary
            </h2>
            <div className="bg-gray-50 rounded-2xl p-5 space-y-4">
              {cart.length > 0 ? (
                cart.map((item, index) => (
                  <div
                    key={index}
                    className="flex justify-between text-gray-700"
                  >
                    <span>
                      {item.name} × {item.quantity}
                    </span>
                    <span className="font-medium">
                      ₦{(item.price * item.quantity).toLocaleString()}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic">No items found</p>
              )}

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal:</span>
                  <span>₦{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Charge Fee:</span>
                  <span>₦{chargeFee.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-green-700 border-t pt-3">
                  <span>Total Paid:</span>
                  <span>₦{total.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="mb-10">
            <h2 className="text-xl font-semibold text-green-700 mb-4 flex items-center gap-3">
              📋 Next Steps to Complete Your Service
            </h2>
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 space-y-4">
              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-sm shrink-0">1</div>
                <div>
                  <p className="font-semibold text-gray-800">Proceed to the Payment Booth</p>
                  <p className="text-gray-600 text-sm mt-1">Visit any UCH Pay1One payment booth located within the hospital premises.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-sm shrink-0">2</div>
                <div>
                  <p className="font-semibold text-gray-800">Inform the Teller</p>
                  <p className="text-gray-600 text-sm mt-1">Tell the teller you used <strong>QuickPay</strong> and present your <strong>Transaction Reference: {reference}</strong>. This code is valid for a single use only.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-sm shrink-0">3</div>
                <div>
                  <p className="font-semibold text-gray-800">Collect Your Official Receipt</p>
                  <p className="text-gray-600 text-sm mt-1">The teller will verify your payment and issue you an official hospital receipt.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-sm shrink-0">4</div>
                <div>
                  <p className="font-semibold text-gray-800">Proceed to the Service Point</p>
                  <p className="text-gray-600 text-sm mt-1">With your receipt in hand, go to the relevant department or service point to receive your hospital service.</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-green-200 text-sm text-green-700">
                📧 A confirmation email with these steps and your receipt has been sent to <strong>{customerDetails?.payer_email}</strong>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <button
              onClick={() => setShowReceiptModal(true)}
              className="bg-white border-2 border-green-600 text-green-700 font-semibold py-4 px-6 rounded-2xl hover:bg-green-50 transition flex items-center justify-center gap-2"
            >
              📄 View Receipt
            </button>

            <button
              onClick={handleCreateWallet}
              className="bg-white border-2 border-green-600 text-green-700 font-semibold py-4 px-6 rounded-2xl hover:bg-green-50 transition flex items-center justify-center gap-2"
            >
              💰 Create Wallet
            </button>

            <button
              onClick={handleBackToHome}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-2xl transition flex items-center justify-center gap-2"
            >
              ← Back to Home
            </button>
          </div>

          <p className="text-center text-gray-500 text-sm mt-8">
            Thank you for choosing UCH Self Service
          </p>
        </div>
      </main>

      {/* ==================== RECEIPT MODAL ==================== */}
      {showReceiptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">

            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-green-700">
                Payment Receipt
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={handleDownloadPDF}
                  className="px-5 py-2 bg-green-700 text-white text-sm font-semibold rounded-lg hover:bg-green-800 transition flex items-center gap-2"
                >
                  ↓ Download PDF
                </button>
                <button
                  onClick={() => setShowReceiptModal(false)}
                  className="px-5 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Receipt Content — Printable Area */}
            <div id="receipt-content" className="p-8 overflow-auto flex-1 text-sm">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-green-700">
                  UCH Self Service
                </h3>
                <p className="text-gray-500 mt-1">Official Payment Receipt</p>
                <div className="h-px bg-gray-300 my-6"></div>
              </div>

              <div className="space-y-6">
                {/* Patient Info */}
                <div>
                  <p className="font-semibold text-gray-700 mb-2">
                    PATIENT INFORMATION
                  </p>
                  <div className="grid grid-cols-2 gap-y-2 text-gray-600">
                    <p>Name:</p>
                    <p className="font-medium text-right">
                      {customerDetails?.patient_first_name}{" "}
                      {customerDetails?.patient_last_name}
                    </p>
                    <p>Patient ID:</p>
                    <p className="font-medium text-right">
                      {customerDetails?.patient_hospital_number || "N/A"}
                    </p>
                    {customerDetails?.patient_email && (<><p>Email:</p><p className="font-medium text-right">{customerDetails.patient_email}</p></>)}
                    {customerDetails?.patient_phone_number && (<><p>Phone:</p><p className="font-medium text-right">{customerDetails.patient_phone_number}</p></>)}
                  </div>
                </div>

                {/* Payer Info */}
                <div>
                  <p className="font-semibold text-gray-700 mb-2">
                    PAYER INFORMATION
                  </p>
                  <div className="grid grid-cols-2 gap-y-2 text-gray-600">
                    <p>Name:</p>
                    <p className="font-medium text-right">
                      {customerDetails?.payer_first_name}{" "}
                      {customerDetails?.payer_last_name}
                    </p>
                    <p>Email:</p>
                    <p className="font-medium text-right">
                      {customerDetails?.payer_email || "N/A"}
                    </p>
                    <p>Phone:</p>
                    <p className="font-medium text-right">
                      {customerDetails?.payer_phone_number || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Transaction Info */}
                <div>
                  <p className="font-semibold text-gray-700 mb-2">
                    TRANSACTION DETAILS
                  </p>
                  <div className="grid grid-cols-2 gap-y-2 text-gray-600">
                    <p>Transaction Reference:</p>
                    <p className="font-bold text-right font-mono text-[#0d3d2e]">{reference}</p>
                    {rrr !== "—" && (
                      <>
                        <p>RRR (Remita):</p>
                        <p className="font-bold text-right font-mono text-[#0d3d2e]">{rrr}</p>
                      </>
                    )}
                    <p>Date:</p>
                    <p className="font-medium text-right">{currentDate} {currentTime}</p>
                    <p>Description:</p>
                    <p className="font-medium text-right">
                      {customerDetails?.transaction_description || "Hospital Service Payment"}
                    </p>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <p className="font-semibold text-gray-700 mb-3">
                    SERVICES PAID FOR
                  </p>
                  <div className="border rounded-xl overflow-hidden">
                    {cart.map((item, index) => (
                      <div
                        key={index}
                        className="flex justify-between px-4 py-3 border-b last:border-b-0 bg-gray-50"
                      >
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-xs text-gray-500">
                            Qty: {item.quantity}
                          </p>
                        </div>
                        <p className="font-medium">
                          ₦{(item.price * item.quantity).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-gray-100 rounded-xl p-5 space-y-3">
                  <div className="flex justify-between text-gray-700">
                    <span>Subtotal</span>
                    <span>₦{subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>Charge Fee</span>
                    <span>₦{chargeFee.toLocaleString()}</span>
                  </div>
                  <div className="border-t border-gray-300 pt-3 flex justify-between text-lg font-bold text-green-700">
                    <span>TOTAL PAID</span>
                    <span>₦{total.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="text-center mt-10 text-gray-400 text-xs">
                Thank you for your payment • This is a computer-generated receipt
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}